import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';

import { MediaContainer, PageSlotModule } from '@spartacus/storefront';
import { CmsService } from '@spartacus/core';

import { ArgumentMapper, BrandCornerMapper, HeadingParagraphMapper, PushCardMapper, TeaserMapper } from '@seb/web-common/core/mappers';
import { CategoryAdditionalMarketingDataPositionEnum, CategoryAdditionalMarketingDataTypeEnum } from '@seb/web-common/core/enums';
import { CustomCategoryPageMetaResolver } from '@seb/web-common/core/resolvers/custom-category-page-meta.resolver';
import { SdArgumentsComponent, IArguments, ArgumentsLayoutEnum } from '@seb/web-design-system/arguments';
import { CategoryPageConfig, CustomCategory, CustomMedia, FaqEntry } from '@seb/web-common/core/models';
import { BaseFaqComponent } from '@seb/web-common/core/components/base/base-faq/base-faq.component';
import { ChatService, ImageService, LazyLoadingService } from '@seb/web-common/core/services';
import { ITeaser, SdTeaserComponent } from '@seb/web-design-system/teaser';

import {
  BrandThemesEnum, IComponentWrapper, IExtendedLink, ILinkMedia, IPushCard, PositionEnum, PushSizeEnum,
  SdComponentWrapperComponent, SdPushCardComponent, SdThemeWrapperComponent, ThemesEnum
} from '@seb/web-design-system/core';

import { AbstractCategoryPageComponent } from '../abstract-category-page/abstract-category-page.component';
import { SubCategoryComponent } from '../../components/sub-category/sub-category.component';

@Component({
  selector: 'wc-category-page',
  templateUrl: './category-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdPushCardComponent,
    SdTeaserComponent,
    SdThemeWrapperComponent,
    PageSlotModule,
    SubCategoryComponent,
    SdArgumentsComponent,
    BaseFaqComponent
  ]
})
export class CategoryPageComponent extends AbstractCategoryPageComponent implements OnInit {
  ArgumentsLayoutEnum = ArgumentsLayoutEnum;
  BrandThemesEnum = BrandThemesEnum;
  PushSizeEnum = PushSizeEnum;
  PositionEnum = PositionEnum;
  ThemesEnum = ThemesEnum;

  pushLame1: IPushCard;
  argumentsLame7: IArguments;
  lameConfig: CategoryPageConfig;
  faqWrapper: IComponentWrapper;
  faqs: FaqEntry[];
  subcategories: CustomCategory[];
  teasers: ITeaser[] = [];
  textConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.center;
  mediaContainerSubscription: Subscription;
  lame7MediaSubscription: Subscription;
  isAccessoryCategory: boolean;
  contentWrapper: IComponentWrapper;
  subCatTextConfig: PositionEnum.center | PositionEnum.left | PositionEnum.right;
  isDescription: boolean;
  isFit: boolean;
  hasModal: boolean;
  lame1Loaded = false;
  eagerLoaded = false;
  isLazyLoadingEnabled = true;
  lame1ThemeConfig: string;
  teaserConfig: boolean;
  linkCardSubcategories: boolean;

  constructor(
    private translateService: TranslateService,
    private pushCardMapper: PushCardMapper,
    private argumentMapper: ArgumentMapper,
    private imageService: ImageService,
    private customCategoryPageMetaResolver: CustomCategoryPageMetaResolver,
    private cmsService: CmsService,
    private lazyLoadingService: LazyLoadingService,
    private chatService: ChatService,
    private teaserMapper: TeaserMapper,
    private brandCornerMapper: BrandCornerMapper,
    private headingParagraphMapper: HeadingParagraphMapper
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.isLazyLoadingEnabled = this.lazyLoadingService.isEnabled;
    this.handleEagerLoadingUids();

    this.subscriptions.add(
      this.lazyLoadingService.canLazyLoad.subscribe((canLazyLoad) => {
        this.eagerLoaded = canLazyLoad;
        this.cdRef.markForCheck();
      })
    );

    this.setConfig();

    this.faqWrapper = {
      heading: this.headingParagraphMapper.toHeadingParagraph({
        heading: {
          headline: this.translateService.instant('categoryPage.sectionTitles.faq')
        },
        titleLevel: 2,
        textConfig: PositionEnum.center
      })
    };
  }

  protected onCategoryChanged(): void {
    this.initLame1();
    this.initSubcategories();
    this.isAccessoryCategory = this.category.url?.includes('accessory');
    this.faqs = this.category.faqs?.faqEntries;
    this.initLame7();
  }

  protected onBrandCornerChanged(): void {
    this.setConfig();
  }

  onClick(event: IExtendedLink | ILinkMedia): void {
    if (event?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
  }

  getContentWrapper(wrapper: IComponentWrapper): void {
    this.contentWrapper = wrapper;
  }

  private setConfig(): void {
    const wcConfig = this.brandCornerMapper.toWcConfig(this.brandCorner);

    this.linkCardSubcategories = wcConfig.linkCardSubcategories;
    this.teaserConfig = wcConfig.isTeaserEmbedded;
    this.lameConfig = wcConfig.categoryPage;

    this.subCatTextConfig = this.lameConfig?.lame4?.textConfig;
    this.lame1ThemeConfig = this.lameConfig?.lame1?.theme;
  }

  private initSubcategories(): void {
    if (this.category.subCategories) {
      this.subcategories = this.category.subCategories.map((categ) => ({
        ...categ,
        brandLogo: this.brandLogo
      }));
    } else {
      this.subcategories = [];
    }
  }

  private initLame1(): void {
    this.cleanLame1();

    const dataLame1 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_1 &&
      (data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Push_Card_Component ||
        data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Teaser)
    );

    if (this.isAccessoryCategory && dataLame1) {
      this.subscriptions.add(
        this.customCategoryPageMetaResolver.resolveTitle().subscribe((titleTag) => {
          dataLame1.headline = titleTag;
          this.cdRef.markForCheck();
        })
      );
    }

    if (dataLame1) {
      switch (dataLame1.typeSection) {
        case CategoryAdditionalMarketingDataTypeEnum.Push_Card_Component:
          if (dataLame1.mediaContainer) {
            const pushCustomMedia = this.getMediaFromAdditionalMarketing(dataLame1.mediaContainer);
            this.mediaContainerSubscription?.unsubscribe();
            this.mediaContainerSubscription = this.getMediaContainer(pushCustomMedia).subscribe((media) => {
              this.pushLame1 = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(dataLame1, media, this.brandLogo);
              this.lame1Loaded = true;
              this.cdRef.markForCheck();
            });
          } else {
            this.pushLame1 = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(dataLame1, undefined, this.brandLogo);
            this.lame1Loaded = true;
          }
          this.isDescription = dataLame1.isDescription;
          this.isFit = dataLame1.isFit;
          this.hasModal = dataLame1.hasModal;
          this.cdRef.markForCheck();
          break;
        case CategoryAdditionalMarketingDataTypeEnum.Teaser:
          dataLame1.additionalMarketingPushs?.forEach((data, index) => {
            data.brandLogo = this.brandLogo;
            if (data.mediaContainer) {
              const customMedia = this.getMediaFromAdditionalMarketing(data.mediaContainer);
              this.mediaContainerSubscription?.unsubscribe();
              this.mediaContainerSubscription = this.getMediaContainer(customMedia).subscribe((media) => {
                this.teasers[index] = this.teaserMapper.toITeaser(data, this.lame1ThemeConfig, this.teaserConfig, media, this.textConfig);
                this.lame1Loaded = true;
                this.cdRef.markForCheck();
              });
            } else {
              this.teasers[index] = this.teaserMapper.toITeaser(data, this.lame1ThemeConfig, this.teaserConfig, undefined, this.textConfig);
              this.lame1Loaded = true;
              this.cdRef.markForCheck();
            }
          });
          break;
      }
    } else {
      this.pushLame1 = undefined;
      this.lame1Loaded = true;
      this.cdRef.markForCheck();
    }
  }

  private handleEagerLoadingUids(): void {
    this.subscriptions.add(
      this.cmsService.getContentSlot('DynamicBody1').subscribe((slotData) => {
        const uid: string = slotData?.components?.[0]?.uid;
        this.lazyLoadingService.setFirstUids(uid ? [uid] : []);
      })
    );
  }

  private cleanLame1(): void {
    this.teasers = [];
    this.pushLame1 = undefined;
    this.lame1Loaded = false;
  }

  private initLame7(): void {
    this.lame7MediaSubscription?.unsubscribe();

    const dataLame7 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_7 &&
      data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Argument
    );

    if (dataLame7) {
      if (dataLame7.mediaContainer) {
        const customMedia = this.getMediaFromAdditionalMarketing(dataLame7.mediaContainer);
        this.lame7MediaSubscription?.unsubscribe();
        this.lame7MediaSubscription = this.getMediaContainer(customMedia).subscribe((media) => {
          this.argumentsLame7 = this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame7, media);
          this.cdRef.markForCheck();
        });
      } else {
        this.argumentsLame7 = this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame7);
        this.cdRef.markForCheck();
      }
    } else {
      this.argumentsLame7 = undefined;
      this.cdRef.markForCheck();
    }
  }

  private getMediaContainer(media: CustomMedia): Observable<CustomMedia> {
    return this.imageService.getMediaFromContainer(media as MediaContainer);
  }

  private getMediaFromAdditionalMarketing(mediaContainer: CustomMedia[]): CustomMedia {
    if (!mediaContainer) {
      return undefined;
    }
    return mediaContainer.reduce((media, currentMedia) => {
      const m = { ...media, [currentMedia.format]: { ...currentMedia } };
      delete m.format;
      return m;
    }, {});
  }
}
